import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import {
  getAbcDistribution,
  recalculateAbc,
  valueShare,
  type AbcDistributionDto,
} from './abcApi';

const ABC_KEY = 'abc-distribution';

interface Props {
  warehouseId: string;
  canWrite: boolean;
  /** Called after a run that wrote a snapshot, so the grid's class column refreshes. */
  onClassified: () => void;
}

/**
 * ABC classification for one warehouse: what the last run produced, and a button to run it
 * again.
 *
 * The class SIZES are the point of this card. The configured bands are shares of movement
 * value, so how many items land in each class depends entirely on the warehouse's own movement
 * curve — which means the only way to tell whether the bands are sensible is to run it and
 * look. The legacy product offered no such feedback, and its parameters were never set.
 */
export function AbcSummaryCard({ warehouseId, canWrite, onClassified }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const stored = useQuery({
    queryKey: [ABC_KEY, warehouseId] as const,
    queryFn: ({ signal }) => getAbcDistribution(warehouseId, signal),
    enabled: Boolean(warehouseId),
  });

  const run = useMutation({
    mutationFn: () => recalculateAbc(warehouseId),
    onSuccess: (result) => {
      queryClient.setQueryData([ABC_KEY, warehouseId], result);
      // Only a run that actually wrote a snapshot changes the grid's class column. Refetching
      // after a refusal would spend a request to learn nothing changed.
      if (result.outcome === 'classified') onClassified();
    },
  });

  // The freshest thing we know: a run just completed, or else whatever is stored. A run's
  // result carries itemsWithMovementButNoCost, which a stored snapshot cannot.
  const d: AbcDistributionDto | null = run.data ?? stored.data ?? null;

  // Null means "not recorded" (a stored snapshot), which for display purposes is the same as
  // having none to report. Narrowed once here so the interpolation is a plain number.
  const uncosted = d?.itemsWithMovementButNoCost ?? 0;

  const bands = useMemo(() => {
    if (!d || d.outcome !== 'classified') return [];
    return [
      { key: 'A', count: d.classACount, dollars: d.classADollars, color: 'red' },
      { key: 'B', count: d.classBCount, dollars: d.classBDollars, color: 'yellow' },
      { key: 'C', count: d.classCCount, dollars: d.classCDollars, color: 'gray' },
    ];
  }, [d]);

  const money = (value: number, currency: string): string => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      // An unrecognised currency code must not take the card down with it.
      return `${Math.round(value)} ${currency}`;
    }
  };

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Text fw={600}>{t('planning.abc.title')}</Text>
            <Text size="xs" c="dimmed">
              {t('planning.abc.subtitle')}
            </Text>
          </div>
          <Tooltip label={canWrite ? t('planning.abc.recalculateHint') : t('planning.readOnly')}>
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => run.mutate()}
              loading={run.isPending}
              disabled={!canWrite || !warehouseId}
            >
              {t('planning.abc.recalculate')}
            </Button>
          </Tooltip>
        </Group>

        {stored.isLoading && !run.data && <Loader size="sm" />}

        {run.isError && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {recalculateErrorMessage(run.error, t)}
          </Alert>
        )}

        {/* Never run. Said plainly, and said to be optional: a small warehouse that counts by
            walking the aisles needs nothing on this card, and an empty panel with no
            explanation reads as something broken or unfinished. */}
        {!d && !stored.isLoading && !run.isError && (
          <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">{t('planning.abc.neverCalculated')}</Text>
            <Text size="sm" mt={4}>
              {t('planning.abc.optional')}
            </Text>
          </Alert>
        )}

        {d && d.outcome === 'classified' && (
          <>
            <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
              {bands.map((b) => (
                <Card key={b.key} withBorder padding="xs" bg="var(--mantine-color-body)">
                  <Group gap="xs" align="center">
                    <Badge color={b.color} size="sm">
                      {b.key}
                    </Badge>
                    {/* The count as a bare number under a static label, rather than "62 items"
                        interpolated into a sentence. Class A of exactly one item is a real and
                        documented outcome, so any phrasing with a count-noun would need plural
                        agreement in four languages to avoid printing "1 items". */}
                    <Text size="lg" fw={600}>
                      {b.count}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {t('planning.abc.itemsLabel')}
                    </Text>
                  </Group>
                  {/* Both numbers, because either alone misleads: the item count without the
                      value share hides why class A is small, and the share without the count
                      hides whether it is a workable set to count. */}
                  <Text size="xs" c="dimmed" mt={4}>
                    {t('planning.abc.valueShare', {
                      percent: valueShare(b.dollars, d.totalDollarMovement).toFixed(1),
                      value: money(b.dollars, d.currencyCode),
                    })}
                  </Text>
                </Card>
              ))}
            </SimpleGrid>

            <Text size="xs" c="dimmed">
              {t('planning.abc.calculatedAt', {
                date: new Date(d.snapshotDateUtc!).toLocaleString(),
                days: d.movementPeriodDays,
                total: money(d.totalDollarMovement, d.currencyCode),
              })}
            </Text>

            {/* The quiet failure mode. These items moved, so they are not idle stock — they
                have no cost, so they scored zero and sank to class C. A genuinely fast-moving
                item somebody forgot to cost gets counted once a year, and nothing else on this
                screen would ever say so. */}
            {uncosted > 0 && (
              <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                <Text size="sm">{t('planning.abc.movedWithoutCost', { items: uncosted })}</Text>
                <Text size="sm" mt={4}>
                  {t('planning.abc.movedWithoutCostConsequence')}
                </Text>
              </Alert>
            )}
          </>
        )}

        {/* The three refusals. Each has a different fix, so each gets its own explanation
            rather than one shared "no data" message that would send people looking in the
            wrong place. */}
        {d && d.outcome !== 'classified' && (
          <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">
              {d.outcome === 'no_items' && t('planning.abc.noItems')}
              {d.outcome === 'no_movement' &&
                t('planning.abc.noMovement', { days: d.movementPeriodDays })}
              {d.outcome === 'no_costs' && t('planning.abc.noCosts', { days: d.movementPeriodDays })}
            </Text>
            {d.outcome === 'no_costs' && uncosted > 0 && (
              <Text size="sm" mt={4}>
                {t('planning.abc.movedWithoutCost', { items: uncosted })}
              </Text>
            )}
            <Text size="sm" mt={4}>
              {t('planning.abc.nothingWritten')}
            </Text>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/**
 * The recalculate failures are configuration problems, not transient ones, so they get their
 * own text — "try again" is wrong advice when the bands do not sum to 100.
 */
function recalculateErrorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    const type = error.problem.type ?? '';
    if (type.endsWith('abc_not_configured')) return t('planning.abc.errors.notConfigured');
    if (type.endsWith('abc_bands_do_not_sum')) return t('planning.abc.errors.bandsDoNotSum');
    if (type.endsWith('abc_invalid_movement_period')) return t('planning.abc.errors.invalidPeriod');
    if (error.status === 404) return t('planning.abc.errors.warehouseNotFound');
  }
  return t('planning.abc.errors.recalculateFailed');
}
